import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '../../../lib/utils';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md font-medium ring-offset-murphy-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        /** Estilo MeHouse: borde primary, fondo suave, texto primary; hover = más relleno + texto contrastante (no sólido verde con texto negro). */
        default:
          'border border-primary bg-primary/5 text-primary hover:bg-primary/10 hover:text-primary-foreground',
        destructive:
          'bg-destructive text-destructive-foreground hover:bg-destructive/90',
        outline:
          'border border-input bg-background hover:bg-accent hover:text-accent-foreground',
        secondary:
          'bg-secondary text-secondary-foreground hover:bg-secondary/80',
        ghost: 'text-foreground hover:bg-accent hover:text-accent-foreground',
        link: 'text-primary underline-offset-4 hover:underline',
      },
      size: {
        // XS: Muy compacto, altura mínima, fuente pequeña, padding reducido
        // Uso: acciones secundarias, listas densas
        xs: 'h-6 min-h-[24px] px-2 py-1 text-xs leading-tight [&_svg]:h-3 [&_svg]:w-3 gap-1.5',
        // SM: Compacto, altura contenida, fuente legible pero pequeña
        // Uso: formularios, filtros
        sm: 'h-8 min-h-[32px] px-3 py-1.5 text-sm leading-snug [&_svg]:h-3.5 [&_svg]:w-3.5 gap-1.5',
        // MD: Tamaño estándar del sistema
        // Uso: acciones principales y formularios comunes
        default: 'h-10 min-h-[40px] px-4 py-2 text-sm leading-normal [&_svg]:h-4 [&_svg]:w-4 gap-2',
        // LG: Altura amplia, padding generoso, fuente más grande
        // Uso: CTA importantes
        lg: 'h-12 min-h-[48px] px-6 py-3 text-base leading-normal [&_svg]:h-5 [&_svg]:w-5 gap-2.5',
        // XL: Altura dominante, padding amplio, fuente destacada
        // Uso: acciones críticas / hero
        xl: 'h-14 min-h-[56px] px-8 py-4 text-lg leading-relaxed [&_svg]:h-5 [&_svg]:w-5 gap-3',
        icon: 'h-10 min-h-[40px] w-10 [&_svg]:h-4 [&_svg]:w-4',
        /** Solo icono, sin caja ni fondo; transparente para que se vea el fondo del contenedor (como el bottom nav). */
        iconBare: 'h-9 w-9 min-h-0 min-w-0 p-0 gap-0 !bg-transparent border-0 shadow-none ring-offset-0 ring-offset-transparent focus-visible:ring-offset-0 rounded-none [&_svg]:h-5 [&_svg]:w-5',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = 'Button';

export { Button, buttonVariants };




